/* ==========================================================================
   Lumen — section-header.js
   Extends the core header. theme.js keeps owning the mobile nav toggle
   (.header__nav-toggle / .header__nav.is-open); this file adds:
   - header geometry -> CSS custom properties (--header-height, --header-bottom)
   - scroll state: .is-scrolled (transparent mode + shadow) and the
     hide-on-scroll-down behavior for the "scroll-up" sticky mode
   - accessible dropdown / mega menu toggling (aria-expanded is the single
     source of truth; CSS :hover only serves as the no-js fallback)
   - .is-nav-open sync on the header when the core opens the mobile nav
   - localization selects submit their form on change
   ========================================================================== */
(function () {
  'use strict';

  var desktopMQ = window.matchMedia('(min-width: 990px)');
  var hoverMQ = window.matchMedia('(hover: hover)');

  function init() {
    var header = document.querySelector('.header[data-header]');
    if (!header || header._lumenHeaderInit) return;
    header._lumenHeaderInit = true;

    var wrapper = header.closest('.shopify-section') || header.parentElement;
    var nav = header.querySelector('.header__nav');
    var navToggle = header.querySelector('.header__nav-toggle');
    var stickyMode = header.getAttribute('data-sticky') || 'always';
    var items = Array.prototype.slice.call(header.querySelectorAll('[data-header-dropdown]'));

    /* -- geometry -> custom properties ------------------------------------ */
    function setMetrics() {
      if (!document.body.contains(header)) return;
      wrapper.style.setProperty('--header-height', header.offsetHeight + 'px');
      var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', bottom + 'px');
    }
    setMetrics();
    if ('ResizeObserver' in window) {
      new ResizeObserver(setMetrics).observe(header);
    } else {
      window.addEventListener('resize', setMetrics);
    }

    /* -- dropdowns --------------------------------------------------------- */
    function setExpanded(li, open) {
      var btn = li.querySelector('.header__menu-toggle');
      if (btn) btn.setAttribute('aria-expanded', String(open));
    }
    function closeAll(except) {
      items.forEach(function (li) {
        if (li !== except) setExpanded(li, false);
      });
    }

    items.forEach(function (li) {
      var btn = li.querySelector('.header__menu-toggle');
      if (!btn) return;

      /* click / tap / Enter / Space — works at every viewport.
         Grace window: on hybrid touch+hover devices a tap fires mouseenter
         (which opens) right before click, so a click shortly after a
         hover-open keeps the menu open instead of instantly re-closing it. */
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        var justHoverOpened = li._hoverOpenedAt && Date.now() - li._hoverOpenedAt < 700;
        li._hoverOpenedAt = 0;
        closeAll(li);
        setExpanded(li, !open || justHoverOpened);
      });

      /* hover-open on desktop pointer devices (kept in aria-expanded so
         assistive tech sees the same state the eyes do) */
      li.addEventListener('mouseenter', function () {
        if (desktopMQ.matches && hoverMQ.matches) {
          li._hoverOpenedAt = Date.now();
          closeAll(li);
          setExpanded(li, true);
        }
      });
      li.addEventListener('mouseleave', function () {
        if (desktopMQ.matches && hoverMQ.matches) setExpanded(li, false);
      });

      /* keyboard: opening on focus mirrors the hover affordance; guard with
         hoverMQ so a tap's focus event can't fight the click toggle */
      li.addEventListener('focusin', function () {
        if (desktopMQ.matches && hoverMQ.matches) {
          closeAll(li);
          setExpanded(li, true);
        }
      });
      li.addEventListener('focusout', function (e) {
        if (desktopMQ.matches && !li.contains(e.relatedTarget)) setExpanded(li, false);
      });
    });

    document.addEventListener('click', function (e) {
      if (document.body.contains(header) && !header.contains(e.target)) closeAll();
    });

    /* Escape closes the innermost open dropdown first; a second Escape then
       reaches the core's document handler and closes the mobile nav. */
    header.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openBtn = header.querySelector('.header__menu-toggle[aria-expanded="true"]');
      if (!openBtn) return;
      e.stopPropagation();
      closeAll();
      openBtn.focus();
    });

    /* -- mobile nav state sync (core toggles .is-open on the nav) ---------- */
    function syncNav() {
      var open = !!(nav && nav.classList.contains('is-open'));
      header.classList.toggle('is-nav-open', open);
      if (open) header.classList.remove('header--hidden');
    }
    if (nav && 'MutationObserver' in window) {
      new MutationObserver(syncNav).observe(nav, { attributes: true, attributeFilter: ['class'] });
    } else if (navToggle) {
      navToggle.addEventListener('click', function () {
        window.setTimeout(syncNav, 0);
      });
    }

    /* -- scroll state -------------------------------------------------------- */
    var lastY = window.scrollY;
    var ticking = false;

    function onScroll() {
      if (!document.body.contains(header)) return;
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 4);

      if (stickyMode === 'scroll-up') {
        var goingDown = y > lastY + 6;
        var goingUp = y < lastY - 6;
        if (y <= header.offsetHeight + 24) {
          header.classList.remove('header--hidden');
        } else if (
          goingDown &&
          !header.classList.contains('is-nav-open') &&
          !header.contains(document.activeElement)
        ) {
          if (!header.classList.contains('header--hidden')) {
            header.classList.add('header--hidden');
            closeAll();
          }
        } else if (goingUp) {
          header.classList.remove('header--hidden');
        }
      }
      lastY = y;

      /* keep the mobile nav panel anchored under the (possibly moving) header */
      var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--header-bottom', bottom + 'px');
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        onScroll();
        ticking = false;
      });
    }, { passive: true });
    onScroll();

    /* -- localization: submit on change ------------------------------------- */
    header.addEventListener('change', function (e) {
      var select = e.target.closest('[data-localization-select]');
      if (select && select.form) select.form.submit();
    });
  }

  init();

  /* Theme editor ------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.header[data-header]')) init();
  });
  /* Selecting a mega block in the editor opens its panel for preview */
  document.addEventListener('shopify:block:select', function (e) {
    var li = e.target.closest('[data-header-dropdown]');
    if (!li) return;
    var btn = li.querySelector('.header__menu-toggle');
    if (btn) btn.setAttribute('aria-expanded', 'true');
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var li = e.target.closest('[data-header-dropdown]');
    if (!li) return;
    var btn = li.querySelector('.header__menu-toggle');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  });
})();
